// src/pages/Pricing/Pricing.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';

const Accordion = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="border border-slate-600 rounded-md mb-4 overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full p-4 text-left bg-slate-700 hover:bg-slate-600 transition-colors flex justify-between items-center"
                aria-expanded={isOpen}
            >
                <span className="font-semibold text-white">{title}</span>
                {isOpen ? (
                    <ChevronUp className="h-5 w-5 text-white" />
                ) : (
                    <ChevronDown className="h-5 w-5 text-white" />
                )}
            </button>
            {isOpen && (
                <div className="p-4 bg-slate-800 text-white overflow-y-auto max-h-96">
                    <div className="pt-16 -mt-16">
                        {children}
                    </div>
                </div>
            )}
        </div>
    );
};

const Pricing = () => {
    const { t, i18n } = useTranslation();
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const [consentGiven, setConsentGiven] = useState(false);
    const currentLanguage = i18n.language;
    
    const baseStripeLink = 'https://buy.stripe.com/7sYaEX6WWfRcb948fId7q00';
    
    const stripeLink = isAuthenticated && user?.email
        ? `${baseStripeLink}?prefilled_email=${encodeURIComponent(user.email)}`
        : baseStripeLink;

    const PLAN_PRICE = "14,90 €";

    // Helper component for the container to avoid code duplication
    const PricingCard = ({ children }) => (
        <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
            <h2 className="text-center text-4xl font-bold text-brand-orange mb-6">
                {PLAN_PRICE}<span className="text-xl text-slate-300 font-normal">/{t('pricing.perMonth')}</span>
            </h2>
		<p className="text-slate-200 text-center mb-2">
                {t('subscribePrompt.trialOffer')}
            </p>
            <p className="text-slate-200 text-center mb-6">
                {t('subscribePrompt.subscribeAfterTrial')}
            </p>
            <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                <li>{t('pricing.feature1')}</li>
                <li>{t('pricing.feature2')}</li>
                <li>{t('pricing.feature3')}</li>
                <li>{t('pricing.feature4')}</li>
                <li>{t('pricing.feature5')}</li>
            </ul>
            {children}
        </div>
    );

    return (
        <div className="min-h-screen bg-brand-dark flex items-center justify-center p-4 pt-32">
            <PricingCard>
                {!isAuthenticated ? (
                    /* CASE 1: LOGGED OUT USER */
                    <div className="flex flex-col space-y-4">
                        <Link to="/signup" className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors">
                            {t('pricing.signupButton')}
                        </Link>
                        <Link to="/login" className="w-full block text-center px-6 py-3 bg-slate-700 text-white rounded-md hover:bg-slate-600 transition-colors">
                            {t('pricing.loginButton')}
                        </Link>
                    </div>
                ) : user?.subscription_status === 'active' ? (
                    /* CASE 2: LOGGED IN & ACTIVE SUBSCRIBER */
                    <div className="text-center">
                        <CheckCircle className="mx-auto h-12 w-12 text-green-400" />
                        <h2 className="mt-4 text-xl font-bold text-white">{t('pricing.alreadySubscribedTitle')}</h2>
                        <p className="mt-2 text-sm text-slate-300 mb-6">{t('pricing.alreadySubscribedDesc')}</p>
                        <button
                            onClick={() => navigate('/purchase-credits')}
                            className="w-full px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                        >
                            {t('pricing.purchaseMoreCreditsButton')}
                        </button>
                    </div>
                ) : (
                    /* CASE 3: LOGGED IN BUT NOT SUBSCRIBED */
                    <div>
                        <div className="mb-6">
                            <label htmlFor="consent" className="flex items-start text-sm text-slate-300 cursor-pointer">
                                <input
                                    id="consent"
                                    type="checkbox"
                                    checked={consentGiven}
                                    onChange={() => setConsentGiven(!consentGiven)}
                                    className="h-4 w-4 mt-1 rounded border-slate-500 bg-slate-700 text-brand-orange focus:ring-brand-orange"
                                />
                                <span className="ml-3">{t('pricing.consentText')}</span>
                            </label>
                        </div>

                        <Accordion title={t("why_consent_title")}>
                            {/* German Content */}
                            {currentLanguage === 'de' && (
                                <div className="prose prose-invert max-w-none p-5">
                                    <h3 className="text-xl text-brand-orange font-extrabold mb-3">Widerrufsbelehrung</h3>
                                    <h4 className="text-lg text-brand-orange font-extrabold mb-2">Widerrufsrecht:</h4>
                                    <p className="text-slate-300 font-light text-base mb-3">Sie haben das Recht, binnen 14 Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen...</p>
                                    <h4 className="text-lg text-brand-orange font-extrabold mb-2">Ausübung des Widerrufsrechts:</h4>
                                    <p className="text-slate-300 font-light text-base mb-3">Um Ihr Widerrufsrecht auszuüben, müssen Sie uns, der Caaise GmbH, Ritterstraße 8, 33602 Bielefeld, E-Mail: ugotai@caaise.com informieren...</p>
                                    <hr className="border-slate-600 my-4" />
                                    <h4 className="text-lg text-brand-orange font-extrabold mb-2">Muster-Widerrufsformular</h4>
                                    <p className="text-slate-300 text-sm">Caaise GmbH, Ritterstr. 8, 33602 Bielefeld</p>
                                </div>
                            )}

                            {/* French Content */}
                            {currentLanguage === 'fr' && (
                                <div className="prose prose-invert max-w-none p-5">
                                    <h3 className="text-xl text-brand-orange font-extrabold mb-3">Information concernant le droit de rétractation</h3>
                                    <h4 className="text-lg text-brand-orange font-extrabold mb-2">Droit de rétractation:</h4>
                                    <p className="text-slate-300 font-light text-base mb-3">Vous avez le droit de vous rétracter du présent contrat dans un délai de 14 jours sans donner de motif...</p>
                                    <hr className="border-slate-600 my-4" />
                                    <h4 className="text-lg text-brand-orange font-extrabold mb-2">Modèle de formulaire</h4>
                                    <p className="text-slate-300 text-sm">Caaise GmbH, Ritterstr. 8, 33602 Bielefeld, Allemagne</p>
                                </div>
                            )}

                            {/* English Content (Default) */}
                            {(currentLanguage !== 'de' && currentLanguage !== 'fr') && (
                                <div className="prose prose-invert max-w-none p-5">
                                    <h3 className="text-xl text-brand-orange font-extrabold mb-3">Information on the Right of Withdrawal</h3>
                                    <h4 className="text-lg text-brand-orange font-extrabold mb-2">Right of Withdrawal:</h4>
                                    <p className="text-slate-300 font-light text-base mb-3">You have the right to withdraw from this contract within 14 days without giving any reason...</p>
                                    <hr className="border-slate-600 my-4" />
                                    <h4 className="text-lg text-brand-orange font-extrabold mb-2">Model Withdrawal Form</h4>
                                    <p className="text-slate-300 text-sm">Caaise GmbH, Ritterstr. 8, 33602 Bielefeld, Germany</p>
                                </div>
                            )}
                        </Accordion>

                        {consentGiven ? (
                            <div className="mt-6 flex flex-col sm:flex-row gap-4">
                                <a
                                    href={stripeLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 text-center px-4 py-3 bg-slate-800 border-2 border-slate-600 text-white font-semibold rounded-md hover:border-brand-orange transition-colors"
                                >
                                    💳 {t('pricing.stripeButton')}
                                </a>
                                <button
                                    onClick={() => navigate('/paypal')}
                                    className="flex-1 text-center px-4 py-3 bg-slate-800 border-2 border-slate-600 text-white font-semibold rounded-md hover:border-brand-orange transition-colors"
                                >
                                    🅿️ {t('pricing.paypalButton')}
                                </button>
                            </div>
                        ) : (
                            <div className="w-full block text-center px-6 py-3 bg-slate-700 text-white font-semibold rounded-md opacity-50 cursor-not-allowed">
                                {t('pricing.subscribeButton')}
                            </div>
                        )}
                    </div>
                )}
            </PricingCard>
        </div>
    );
};

export default Pricing;
